/**
 * "SEND IT AGAIN" - THE WAIT, AND WHY THERE IS ONE
 * -------------------------------------------------
 * Added 24 August 2026, because somebody signing up pressed the button, saw
 * nothing change, and pressed it again. Three times. That is not impatience -
 * it is the only sensible reading of a button that does not react.
 *
 * TWO THINGS WERE WRONG AND THEY ARE DIFFERENT PROBLEMS.
 *
 * 1. Nothing said the email had gone. The button went back to how it looked
 *    before, which is indistinguishable from a button that did nothing.
 * 2. Nothing stopped them asking again immediately - so a person who was not
 *    sure would send themselves four confirmation emails in ten seconds, each
 *    one invalidating the last, out of an allowance of 300 for the whole day.
 *
 * This hook fixes the second and makes the first possible: while the clock is
 * running, the button says how long is left, which is a thing that is
 * obviously happening.
 *
 * WHY THE CLOCK SURVIVES A RELOAD. It is kept in localStorage, not in memory.
 * Somebody who is not sure whether the mail was sent will refresh the page to
 * check - and a wait that resets when you refresh is not a wait, it is a
 * decoration. The key is per person and per kind of email, so waiting for a
 * confirmation link does not also block a parent's consent request.
 *
 * WHY IT IS NOT THE REAL PROTECTION. The server has rate limiters and they
 * are what actually holds - six confirmation emails an hour, thirty seconds
 * between moderator codes. This is in front of them, so that the ordinary
 * person never meets a limiter at all. Anybody who wants to get past this can
 * clear their browser storage; they then meet the thing that matters.
 *
 * WHY TWO MINUTES, AND WHERE IT IS NOT. Two minutes is long enough that mail
 * has genuinely had a chance to arrive and short enough that somebody whose
 * mail really did not arrive is not stuck. The exception is a moderator's
 * six-digit login code: it dies after ten minutes, so making them wait two of
 * those to ask for another would spend a fifth of its life on the wait. That
 * one uses the thirty seconds the server already enforces.
 */

import { useCallback, useEffect, useState } from 'react';

export const DEFAULT_RESEND_SECONDS = 120;

/** Where one person's clock for one kind of email is kept. */
function storageKey(key) {
  return `cc.resend.${key}`;
}

/** Seconds left on a stored deadline, or 0. Never throws - private browsing. */
function readRemaining(key) {
  try {
    const until = Number(window.localStorage.getItem(storageKey(key)));
    if (!until || Number.isNaN(until)) return 0;
    return Math.max(0, Math.ceil((until - Date.now()) / 1000));
  } catch {
    return 0;
  }
}

/**
 * @param key      what is being waited for - 'confirm-email', 'consent'.
 *                 Include the person's id where two accounts might share a
 *                 browser.
 * @param seconds  how long to wait. Defaults to two minutes.
 *
 * Returns { secondsLeft, ready, start, label }.
 *   start()   begin the wait - call it after a send actually succeeded, never
 *             before, or a failed send locks somebody out of retrying.
 *   label     "1:59", ready to put straight on a button.
 */
export function useResendCooldown(key, seconds = DEFAULT_RESEND_SECONDS) {
  const [secondsLeft, setSecondsLeft] = useState(() => readRemaining(key));

  // Tick while there is something to count down. The interval is cleared as
  // soon as it reaches zero, so an idle screen is not running a timer for
  // ever.
  useEffect(() => {
    if (secondsLeft <= 0) return undefined;

    const timer = setInterval(() => {
      const left = readRemaining(key);
      setSecondsLeft(left);
      if (left <= 0) clearInterval(timer);
    }, 1000);

    return () => clearInterval(timer);
  }, [key, secondsLeft]);

  // A different key means a different wait - re-read rather than carrying the
  // old number across.
  useEffect(() => {
    setSecondsLeft(readRemaining(key));
  }, [key]);

  const start = useCallback(() => {
    try {
      window.localStorage.setItem(storageKey(key), String(Date.now() + seconds * 1000));
    } catch {
      // Private browsing refused it. The countdown then lives only in memory
      // for this page, which is worse than nothing but far better than
      // refusing to send the email.
    }
    setSecondsLeft(seconds);
  }, [key, seconds]);

  const minutes = Math.floor(secondsLeft / 60);
  const label = `${minutes}:${String(secondsLeft % 60).padStart(2, '0')}`;

  return { secondsLeft, ready: secondsLeft <= 0, start, label };
}

export default useResendCooldown;
